import {
    autoUpdate,
    flip,
    FloatingPortal,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
} from "@floating-ui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";

type TooltipLabelProps = {
    tooltip: string;
    align: "center" | "end";
};

const placementByAlign: Record<TooltipLabelProps["align"], "bottom" | "bottom-end"> = {
    center: "bottom",
    end: "bottom-end",
};

export const TooltipLabel: React.FC<TooltipLabelProps> = ({ tooltip, align }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: placementByAlign[align],
        middleware: [offset(6), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const { getReferenceProps, getFloatingProps } = useInteractions([
        useHover(context),
        useClick(context),
        useDismiss(context),
    ]);

    return (
        <span className="mt-[-2px] inline-flex items-center align-middle">
            <span
                ref={refs.setReference}
                {...getReferenceProps()}
                className="inline-flex cursor-pointer align-middle text-gray-500 hover:text-gray-700">
                <InformationCircleIcon className="h-4 w-4" />
            </span>
            {isOpen ? (
                <FloatingPortal>
                    <div
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                        className="z-[1600] w-56 rounded-md bg-gray-800 p-2 text-sm font-normal normal-case text-white shadow-md">
                        {tooltip}
                    </div>
                </FloatingPortal>
            ) : null}
        </span>
    );
};
